import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useUniqueId } from '../../../hooks/use-unique-id'
import { queryClient } from '../../../queryClient'
import { useRequiredContext } from '../../../utils/use-required-context'
import { GroupedSearchOptions, SearchDropdownProps, SearchOption, UNGROUPED } from './index'
import { mergeDefined } from '../../../helpers/merge-defined'
import { defaultFilter } from 'cmdk'

/** Internal SearchDropdown state shared context */
type SearchDropdownInternal = {
  search: string
  onSearch: (search: string) => void
  open: boolean
  onOpen: (open: boolean) => void
  openAndPropagate: () => void
  onSelect: (value: string | null) => void
  selectedLabelAsync: ReturnType<typeof useSelectedLabelAsync>
  labels: NonNullable<Required<SearchDropdownProps['labelConfig']>>
  variant: NonNullable<SearchDropdownProps['variant']>
  derivedOptions: ReturnType<typeof useDerivedOptions>
  defaultFilter: typeof defaultFilter
  props: SearchDropdownProps
}

const SearchDropdownInternalContext = createContext<SearchDropdownInternal | null>(null)

/** Only for internal use. Note that subscribing to this will currently cause rerenders on every update. */
export const useSearchDropdownController = () => useRequiredContext(SearchDropdownInternalContext)

export function SearchDropdownController(props: SearchDropdownProps & {
  children: React.ReactNode
}) {
  const id = useUniqueId()
  const [ search, onSearch ] = useSearchInputHandling(props)
  const [ open, onOpen, openAndPropagate ] = usePopoverOpenHandling(props)
  const derivedOptions = useDerivedOptions(props.options)
  const cachedOptionLabels = useCachedOptionLabels(derivedOptions.allOptions)
  const selectedLabelAsync = useSelectedLabelAsync(id, props.value, derivedOptions.allOptions, cachedOptionLabels.current, props.getLabelForOptionValue)

  const onSelect = async (currentValue: string | null) => {
    props.onChange?.(currentValue)
    onOpen(false)
  }

  useEffect(() => {
    async function syncSearchInputWithLabel() {
      if (props.value && props.syncSearchWithLabel) {
        try {
          onSearch(await selectedLabelAsync.fetch(props.value))
        } catch (e) {
          console.error("SearchDropdown: failed fetching selected label", e)
          onSearch(props.value || "")
        }
      } else {
        onSearch("")
      }
    }
    void syncSearchInputWithLabel()
  }, [ props.value, props.syncSearchWithLabel ])

  const variant = props.variant ?? "default"

  const labels = mergeDefined({
    placeholder: "",
    noOptionsFound: "No options found.",
    loadingOptions: "Loading options...",
    errorFetchingOptions: "Error fetching options.",
  }, props.labelConfig)

  return (
    <SearchDropdownInternalContext.Provider value={{
      labels,
      variant,
      search,
      onSearch,
      open,
      onOpen,
      onSelect,
      selectedLabelAsync,
      openAndPropagate,
      derivedOptions,
      defaultFilter: props.filterFn ?? defaultFilter,
      props,
    }}>
      {props.children}
    </SearchDropdownInternalContext.Provider>
  )
}

function useSearchInputHandling(props: SearchDropdownProps) {
  if ((props.search !== undefined) !== (props.onSearch !== undefined)) {
    throw new Error("SearchDropdown: 'search' and 'onSearch' must be both defined or both undefined.")
  }
  const [ localSearch, setLocalSearch ] = useState('')

  const search = props.search ?? localSearch

  const onSearch = (value: string) => {
    props.onSearch ? props.onSearch(value) : setLocalSearch(value)
  }

  return [ search, onSearch ] as const
}

function usePopoverOpenHandling(props: SearchDropdownProps) {
  const [ open, setOpen ] = useState(false)

  const onOpen = (open: boolean) => {
    setOpen(open)
    props.onOpenChange?.(open)
  }

  const openAndPropagate = () => {
    setOpen(true)
    props.onOpenChange?.(true)
  }

  return [ open, onOpen, openAndPropagate ] as const
}

function useDerivedOptions(options: SearchOption[] | GroupedSearchOptions) {
  return useMemo(() => {
    const groupedOptions = normalizeToGroupedSearchOptions(options)
    const allOptions = Array.from(groupedOptions).flatMap(([, g]) => g.options)
    return {
      groupedOptions,
      allOptions
    }
  }, [ options ])
}

function normalizeToGroupedSearchOptions(providedOptions: SearchOption[] | GroupedSearchOptions) {
  if (providedOptions instanceof Map) return providedOptions
  else {
    const opts: GroupedSearchOptions = new Map()
    opts.set(UNGROUPED, {
      options: providedOptions
    })
    return opts
  }
}

function useCachedOptionLabels(options: SearchOption[]) {
  const cache = useRef<Map<string, string>>(new Map())
  for (const option of options) {
    if (cache.current.has(option.value)) continue
    cache.current.set(option.value, option.label)
  }
  return cache
}

type SelectedLabelResult
  = { type: "success", value: string }
  | { type: "loading" }
  | { type: "error", error: Error }

function useSelectedLabelAsync(
  id: string,
  currentValue: string | null,
  allOptions: SearchOption[],
  cachedOptionLabels: Map<string, string>,
  getLabelForOptionValue?: (value: string) => Promise<string>
) {
  const queryOptions = (value: string | null) => ({
    queryKey: ["search-dropdown-label", id, value, ...allOptions.map(o => o.value)],
    queryFn: async (): Promise<string> => {
      if (!value) return ""
      if (cachedOptionLabels.has(value)) return cachedOptionLabels.get(value)!
      const option = allOptions.find(o => o.value === value)
      if (option) return option.label
      if (getLabelForOptionValue) return await getLabelForOptionValue(value)
      throw new Error(`SearchDropdown getLabelForOptionValue not defined`)
    },
    gcTime: 10_000,
    retry: false
  })

  const queryResult = useQuery(queryOptions(currentValue))
  const fetch = (value: string | null) => queryClient.fetchQuery(queryOptions(value))

  const result: SelectedLabelResult = (() => {
    if (queryResult.error) return { type: "error", error: queryResult.error } as const
    if (queryResult.data === undefined) return { type: "loading" } as const
    return { type: "success", value: queryResult.data } as const
  })()

  return { result, fetch }
}
